import { AxiosResponse } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button, Grid, Paper, SxProps, Tab, Typography } from '@mui/material';
import {
    ImperativePanelHandle,
    Panel,
    PanelGroup,
    PanelResizeHandle,
} from 'react-resizable-panels';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    CustomIconButton,
    HelixLoader,
    MemoReadOnlyJsonEditor,
} from '../../components';
import { SINGLE_NAVIGATION_PATHS } from '../../constants/single-navigation-paths';
import { useAuth } from '../../core';
import { useGraphChain } from '../../hooks';
import { draggablePanelStyles, saveCurrentQuery } from '../../lib';
import { downloadFile } from '../export/export-helper';
import { TQLEditorTableView } from './TQLEditorTableView';
import CustomTQLEditorTabPanel from './CustomTQLEditorTabPanel';
import { TQLHistoryDrawer } from './TQLHistoryDrawer';
import { TQLResultsEmptySate } from './TQLResultsEmptySate';
import {
    isUnsupportedTQLQuery,
    TQL_EDITOR_PANELS,
    TQL_EDITOR_TAB_VALUES,
    TQL_HISTORY_QUERY_KEY,
    tqlEditorPageStyles,
} from './tql-editor-helper';
import { Button as BiqButton, Tabs } from '@biq/apex-ui';
import { Icon } from '@iconify/react';
import { TQLEditor } from './TQLEditor';
import { bbedit } from '@uiw/codemirror-theme-bbedit';
import * as XLSX from 'xlsx';
import { TQLUnsupportedQueryView } from './TQLUnsupportedQueryView';

export const TQLEditorPage = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [tqlResults, setTqlResults] = useState<any>();
    const [displayHistoryDrawer, setDisplayHistoryDrawer] =
        useState<boolean>(false);
    const tqlEditorContainerRef = useRef<ImperativePanelHandle>(null);
    const [expanded, setExpanded] = useState<boolean>(true);
    const [unsupportedQuery, setUnsupportedQuery] = useState<boolean>(false);
    const [tqlCode, setTqlCode] = useState<string>('');
    const [tabIndex, setTabIndex] = useState<number>(
        TQL_EDITOR_TAB_VALUES.JSON_TAB.index
    );

    const { mutate: graphChain, isLoading } = useGraphChain();

    const saveQuery = (query: string): void => {
        saveCurrentQuery(TQL_HISTORY_QUERY_KEY, query);
    };

    const handleSuccess = (result: AxiosResponse<any>): void => {
        const rawData: any = result?.data;
        setTqlResults(rawData);
    };

    const fetchDataForCurrentQuery = (currentQuery: string): void => {
        graphChain(
            {
                filter: currentQuery,
                token: getToken(),
                saveQueries: false,
            },
            {
                onSuccess: (response) => {
                    handleSuccess(response);
                    saveQuery(currentQuery);
                },
            }
        );
    };

    useEffect(() => {
        if (location?.state) {
            const tqlQuery = location.state;
            setTqlCode(tqlQuery);
            fetchDataForCurrentQuery(tqlQuery);
        }
    }, [location.state]);

    const toggleEditorView = (): void => {
        globalThis.dispatchEvent(new Event('resize'));
        setExpanded((preVal: boolean): boolean => {
            return !preVal;
        });
    };

    const onRunButtonClick = (): void => {
        if (isUnsupportedTQLQuery(tqlCode)) {
            setUnsupportedQuery(true);
        } else {
            setUnsupportedQuery(false);
            fetchDataForCurrentQuery(tqlCode);
        }
    };

    const onHistoryButtonClick = (): void => {
        setDisplayHistoryDrawer(true);
    };

    const onTQLCodeChange = React.useCallback(
        (value: string) => {
            setTqlCode(value);
        },
        [setTqlCode]
    );

    const onTabChange = (event: React.SyntheticEvent, newValue: any): void => {
        if (event) {
            setTabIndex(newValue);
        }
    };

    const handleResize = (): void => {
        globalThis.dispatchEvent(new Event('resize'));
    };

    const handleDownloadJsonClick = (): void => {
        downloadFile(
            tqlResults,
            `${new Date().toISOString()}.json`,
            'application/json;charset=utf-8;'
        );
    };

    const handleDownloadTableClick = (): void => {
        const dynamicColumnKeys: string[] = Object.keys(tqlResults[0]);
        const transformedData: any[] = [];
        for (const row of tqlResults) {
            let normalizedRow = {};
            for (const key of dynamicColumnKeys) {
                const rowValue = row[key];
                const normalizedValue =
                    typeof rowValue === 'object' || Array.isArray(rowValue)
                        ? JSON.stringify(rowValue)
                        : structuredClone(rowValue);
                normalizedRow = { ...normalizedRow, [key]: normalizedValue };
            }
            transformedData.push(normalizedRow);
        }
        const worksheet = XLSX.utils.json_to_sheet(transformedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, `${new Date().toISOString()}.xlsx`);
    };

    const onHistoryDrawerClose = (): void => {
        setDisplayHistoryDrawer(false);
    };

    const onHistoryQueryClick = (value: string): void => {
        setTqlCode(value);
        onHistoryDrawerClose();
    };

    const getDraggablePanelHandlerStyles = (): SxProps => {
        const baseStyles = tqlEditorPageStyles.panelContainerBaseStyles;

        if (!expanded) return baseStyles;

        if (tqlResults?.length) {
            return {
                ...baseStyles,
                ...tqlEditorPageStyles.visiblePanelContainerStyles,
            };
        }

        return {
            ...baseStyles,
            ...tqlEditorPageStyles.visibleEmptyPanelContainerStyles,
        };
    };

    const handleUnsupportedQueryClick = (): void => {
        navigate(SINGLE_NAVIGATION_PATHS.LIFEGRAPH, { state: tqlCode });
    };

    const displayJSONResultsView = (): React.JSX.Element => {
        if (!unsupportedQuery && tqlResults?.length) {
            return (
                <MemoReadOnlyJsonEditor jsonData={tqlResults} theme={bbedit} />
            );
        } else if (!unsupportedQuery && !tqlResults?.length) {
            return (
                <Paper sx={tqlEditorPageStyles.paper}>
                    <TQLResultsEmptySate />
                </Paper>
            );
        } else {
            return (
                <Paper sx={tqlEditorPageStyles.paper}>
                    <TQLUnsupportedQueryView
                        onClick={handleUnsupportedQueryClick}
                    />
                </Paper>
            );
        }
    };

    const displayTableResultsView = (): React.JSX.Element => {
        if (!unsupportedQuery && tqlResults?.length) {
            return <TQLEditorTableView data={tqlResults} />;
        } else if (!unsupportedQuery && !tqlResults?.length) {
            return (
                <Paper sx={tqlEditorPageStyles.paper}>
                    <TQLResultsEmptySate />
                </Paper>
            );
        } else {
            return (
                <Paper sx={tqlEditorPageStyles.paper}>
                    <TQLUnsupportedQueryView
                        onClick={handleUnsupportedQueryClick}
                    />
                </Paper>
            );
        }
    };

    if (isLoading) {
        return <HelixLoader />;
    }

    return (
        <Grid sx={tqlEditorPageStyles.mainContainer}>
            <Grid
                sx={
                    expanded
                        ? {
                              ...tqlEditorPageStyles.titleAndHideButtonContainer,
                              ...tqlEditorPageStyles.collapsedTitleAndAddButtonContainer,
                          }
                        : {
                              ...tqlEditorPageStyles.titleAndHideButtonContainer,
                              ...tqlEditorPageStyles.titleAndAddButtonContainer,
                          }
                }
            >
                <Grid sx={tqlEditorPageStyles.titleContainer}>
                    <Typography sx={tqlEditorPageStyles.titleText}>
                        TQL Editor
                    </Typography>
                    <Button
                        onClick={toggleEditorView}
                        sx={tqlEditorPageStyles.filterButton}
                    >
                        <Typography sx={tqlEditorPageStyles.filterButtonText}>
                            {expanded ? 'hide' : 'show'}
                        </Typography>
                        <KeyboardArrowDownIcon
                            sx={{
                                ...tqlEditorPageStyles.arrowDownIcon,
                                rotate: `${expanded ? '180deg' : '0'}`,
                            }}
                        />
                    </Button>
                </Grid>
                <Grid>
                    <BiqButton
                        variant='contained'
                        size='small'
                        onClick={onRunButtonClick}
                        sx={tqlEditorPageStyles.runButton}
                        endIcon={
                            <Icon
                                icon={'mdi:play-circle-outline'}
                                style={tqlEditorPageStyles.buttonIcon}
                            />
                        }
                    >
                        Run
                    </BiqButton>
                    <BiqButton
                        variant='outlined'
                        size='small'
                        onClick={onHistoryButtonClick}
                        sx={tqlEditorPageStyles.historyButton}
                        endIcon={
                            <Icon
                                icon={'mdi:history'}
                                style={tqlEditorPageStyles.buttonIcon}
                            />
                        }
                    >
                        History
                    </BiqButton>
                </Grid>
            </Grid>
            <PanelGroup direction='vertical' onResizeCapture={handleResize}>
                {expanded && (
                    <Panel
                        order={TQL_EDITOR_PANELS.EDITOR_PANEL.ORDER}
                        defaultSize={
                            TQL_EDITOR_PANELS.EDITOR_PANEL.DEFAULT_SIZE
                        }
                        maxSize={TQL_EDITOR_PANELS.EDITOR_PANEL.MAX_SIZE}
                        minSize={TQL_EDITOR_PANELS.EDITOR_PANEL.MIN_SIZE}
                        collapsedSize={
                            TQL_EDITOR_PANELS.EDITOR_PANEL.COLLAPSED_SIZE
                        }
                        ref={tqlEditorContainerRef}
                        style={tqlEditorPageStyles.tqlEditorPanel}
                        id='tql-editor-panel'
                    >
                        <TQLEditor
                            tqlCode={tqlCode}
                            onTQLCodeChange={onTQLCodeChange}
                        />
                    </Panel>
                )}
                <Panel
                    order={TQL_EDITOR_PANELS.RESULTS_PANEL.ORDER}
                    maxSize={TQL_EDITOR_PANELS.RESULTS_PANEL.MAX_SIZE}
                    defaultSize={TQL_EDITOR_PANELS.RESULTS_PANEL.DEFAULT_SIZE}
                    minSize={TQL_EDITOR_PANELS.RESULTS_PANEL.MIN_SIZE}
                    style={tqlEditorPageStyles.resultsPanel}
                    id='results-panel'
                >
                    <>
                        <Grid sx={tqlEditorPageStyles.tabsContainer}>
                            <Tabs
                                color='secondary'
                                orientation='horizontal'
                                tabsType='filled'
                                variant='standard'
                                withIcon={false}
                                value={tabIndex}
                                onChange={onTabChange}
                                scrollButtons={true}
                                sx={tqlEditorPageStyles.tabs}
                            >
                                <Tab
                                    label={TQL_EDITOR_TAB_VALUES.JSON_TAB.label}
                                />
                                <Tab
                                    label={
                                        TQL_EDITOR_TAB_VALUES.TABLE_TAB.label
                                    }
                                />
                            </Tabs>
                            {!!tqlResults?.length && (
                                <Grid
                                    sx={
                                        tqlEditorPageStyles.downloadIconContainer
                                    }
                                >
                                    <CustomIconButton
                                        title='Download JSON'
                                        className='download-icon'
                                        icon='mdi:file-download'
                                        onClick={handleDownloadJsonClick}
                                    />
                                    <CustomIconButton
                                        title='Download Table'
                                        className='download-icon'
                                        icon='mdi:table-download'
                                        onClick={handleDownloadTableClick}
                                    />
                                </Grid>
                            )}
                        </Grid>
                        <Grid item sx={getDraggablePanelHandlerStyles()}>
                            {expanded && (
                                <PanelResizeHandle
                                    style={
                                        draggablePanelStyles.panelResizeHandleStyles
                                    }
                                    disabled={!expanded}
                                />
                            )}
                        </Grid>
                        <CustomTQLEditorTabPanel
                            index={TQL_EDITOR_TAB_VALUES.JSON_TAB.index}
                            value={tabIndex}
                            customStyles={
                                tqlResults?.length
                                    ? tqlEditorPageStyles.populatedCustomTQLPanel
                                    : tqlEditorPageStyles.emptyCustomTQLPanel
                            }
                        >
                            {displayJSONResultsView()}
                        </CustomTQLEditorTabPanel>
                        <CustomTQLEditorTabPanel
                            index={TQL_EDITOR_TAB_VALUES.TABLE_TAB.index}
                            value={tabIndex}
                            customStyles={
                                tqlResults?.length
                                    ? tqlEditorPageStyles.populatedCustomTQLPanel
                                    : tqlEditorPageStyles.emptyCustomTQLPanel
                            }
                        >
                            {displayTableResultsView()}
                        </CustomTQLEditorTabPanel>
                    </>
                </Panel>
            </PanelGroup>
            {displayHistoryDrawer && (
                <TQLHistoryDrawer
                    open={displayHistoryDrawer}
                    onClose={onHistoryDrawerClose}
                    onApplyButtonClick={onHistoryQueryClick}
                />
            )}
        </Grid>
    );
};
